import React, { useEffect, useRef, useState } from 'react';
import { MapPinned } from 'lucide-react';

import { Checkbox } from '../ui/checkbox';
import { FAMILLES_FILTRE, basculerFamille, compterParFamille } from './famillesZones';

/**
 * Petite fenêtre « Zones »: une case à cocher par famille de zones, avec le
 * nombre de zones et la couleur qui les marque sur la carte.
 *
 * À poser dans l'`overlay` de `MapSurface`. L'état (`masquees`, les familles
 * décochées) appartient à l'écran, qui passe la même liste à `ZoneLayer`
 * (`famillesMasquees`). Seules les familles présentes sont listées.
 *
 * `couleurs` : à vrai quand la carte peint les zones par famille
 * (`colorBy="type"`); sinon la couleur raconte autre chose (zone franchie ou à
 * venir) et la pastille serait trompeuse.
 */
export default function ZonesFiltre({ zones, masquees, onChange, couleurs = false }) {
  const [ouverte, setOuverte] = useState(false);
  const racine = useRef(null);
  const compte = compterParFamille(zones);
  const familles = FAMILLES_FILTRE.filter((f) => compte[f.cle] > 0);

  useEffect(() => {
    if (!ouverte) return undefined;
    const fermerAuDehors = (evenement) => {
      if (racine.current && !racine.current.contains(evenement.target)) setOuverte(false);
    };
    const fermerAuClavier = (evenement) => {
      if (evenement.key === 'Escape') setOuverte(false);
    };
    document.addEventListener('mousedown', fermerAuDehors);
    document.addEventListener('keydown', fermerAuClavier);
    return () => {
      document.removeEventListener('mousedown', fermerAuDehors);
      document.removeEventListener('keydown', fermerAuClavier);
    };
  }, [ouverte]);

  if (familles.length === 0) return null;

  const nbMasquees = familles.filter((f) => masquees.includes(f.cle)).length;

  return (
    <div ref={racine} className="relative" data-testid="zones-filtre">
      <button
        type="button"
        aria-expanded={ouverte}
        aria-haspopup="true"
        onClick={() => setOuverte((v) => !v)}
        className="flex items-center gap-1.5 rounded-lg border border-border bg-card/90 px-2 py-1 text-xs font-medium text-foreground shadow-sm backdrop-blur-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <MapPinned className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        Zones
        {nbMasquees > 0 && (
          <span className="rounded-full bg-muted px-1.5 text-[10px] text-muted-foreground">
            {familles.length - nbMasquees}/{familles.length}
          </span>
        )}
      </button>
      {ouverte && (
        <div
          role="group"
          aria-label="Familles de zones"
          className="absolute right-0 top-full z-10 mt-1 w-56 max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-card p-2 shadow-md"
        >
          <ul className="space-y-0.5">
            {familles.map((famille) => {
              const visible = !masquees.includes(famille.cle);
              const identifiant = `zones-famille-${famille.cle}`;
              return (
                <li key={famille.cle}>
                  <label
                    htmlFor={identifiant}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-sm hover:bg-muted"
                  >
                    <Checkbox
                      id={identifiant}
                      checked={visible}
                      onCheckedChange={() => onChange(basculerFamille(masquees, famille.cle))}
                    />
                    {couleurs && (
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: famille.couleur }}
                        aria-hidden="true"
                      />
                    )}
                    <span className="flex-1 text-foreground">{famille.label}</span>
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {compte[famille.cle]}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
          {nbMasquees > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mt-1 w-full rounded-md px-1.5 py-1 text-left text-xs font-medium text-info hover:bg-muted"
            >
              Tout afficher
            </button>
          )}
        </div>
      )}
    </div>
  );
}
